import apiClient from './api.service';

export interface Permission {
  key: string;
  label: string;
  description: string;
}

export interface PermissionCategory {
  category: string;
  permissions: Permission[];
}

export interface Role {
  id: number;
  store_id: number;
  name: string;
  description: string;
  permissions: Permission[] | string[]; // Can be array of Permission objects or IDs
  is_system: boolean;
  users_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateRoleData {
  name: string;
  description: string;
  permissions: string[];
}

export interface UpdateRoleData {
  name: string;
  description: string;
  permissions: string[];
}

/**
 * Role Service
 * Handles all role-related API calls
 */
class RoleService {
  /**
   * Get all roles for a store
   */
  async getStoreRoles(storeId: number): Promise<Role[]> {
    const response = await apiClient.get(`/roles/${storeId}/roles`);
    return response.data.data.roles;
  }

  /**
   * Get role by ID
   */
  async getRoleById(storeId: number, roleId: number): Promise<Role> {
    const response = await apiClient.get(`/roles/${storeId}/roles/${roleId}`);
    return response.data.data.role;
  }

  /**
   * Get available permissions
   */
  async getAvailablePermissions(): Promise<PermissionCategory[]> {
    const response = await apiClient.get('/roles/permissions');
    return response.data.data.permissions;
  }

  /**
   * Create a new role
   */
  async createRole(storeId: number, roleData: CreateRoleData): Promise<Role> {
    const response = await apiClient.post(`/roles/${storeId}/roles`, roleData);
    return response.data.data.role;
  }

  /**
   * Update a role
   */
  async updateRole(storeId: number, roleId: number, roleData: UpdateRoleData): Promise<Role> {
    const response = await apiClient.put(`/roles/${storeId}/roles/${roleId}`, roleData);
    return response.data.data.role;
  }

  /**
   * Delete a role
   */
  async deleteRole(storeId: number, roleId: number): Promise<void> {
    await apiClient.delete(`/roles/${storeId}/roles/${roleId}`);
  }

  /**
   * Get user permissions for a store
   */
  async getUserPermissions(storeId: number, userId: number): Promise<string[]> {
    const response = await apiClient.get(`/roles/${storeId}/users/${userId}/permissions`);
    return response.data.data.permissions;
  }
}

// Export singleton instance
export default new RoleService();

